// LoginPage — Citycare Property Asset Capture login.
//
// Variant A (Centered card) with the photo-overlay background, default
// logo size, and "friendly" copy tone — chosen from the design review
// in the login design package on 2026-04-30.
//
// Auth model: Microsoft Entra ID is the primary path. The "Continue
// with Microsoft" button is a plain <a> to the backend `/auth/login`
// endpoint (Encore route), which initiates the OAuth Authorization
// Code Flow with PKCE per ADR-012. A client-side Link would route
// inside the SPA and miss the redirect.
//
// Email/password is a secondary fallback per design intent. The
// backend has no email/password handler today (ADR-012 specifies
// Entra-only), so the form is decorative and the submit button is
// disabled. When the endpoint exists, remove the disabled attr and
// wire the onSubmit POST.
//
// Post-login routing: the backend `/auth/callback` issues the session
// cookie and redirects to `redirect_to` (or `/` if absent). The SPA
// then routes admins → /admin/dashboard and capture users → /capture
// based on AuthData.role (see RequireAuth / RequireAdmin). This page
// does NOT pick a destination.

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { ApiError, apiFetch, buildLoginUrl, navigateTo } from '../hooks/api'
import { BrandHeader } from '../ui/brand-header'

// Hero photo bundled at apps/frontend/public/login-hero.jpg — a
// Citycare-managed park / grounds shot that sets the product
// context. The component still treats the image as optional: if the
// file is missing or fails to load, the onError handler below flips
// `imgFailed` and the forest-gradient overlay carries the page on
// its own.
const HERO_IMAGE = '/login-hero.jpg'

// Map the backend's `error` codes to UI copy. Anything we don't
// recognise falls back to a generic message so a future server-side
// addition does not crash the SPA.
function describeCredentialsError(code: string | undefined): string {
  switch (code) {
    case 'invalid_credentials':
      return 'Email or password didn\u2019t match. Try again, or use Microsoft.'
    case 'missing_fields':
      return 'Enter both email and password.'
    case 'not_in_dev_env':
      return 'Email sign-in is only available in development.'
    case 'invalid_json':
      return 'Something went wrong sending your details. Refresh and try again.'
    default:
      return 'Sign-in failed. Try again, or use Microsoft.'
  }
}

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [imgFailed, setImgFailed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // `redirect_to` query param threaded through to the backend
  // `/auth/login` handler so post-login routing returns the user
  // where they came from. When the URL has no explicit redirect_to
  // we deliberately omit the param from the Microsoft href and let
  // the backend fall back to FRONTEND_HOME_URL. Hardcoding "/" here
  // would route every user through `<Navigate to="/admin/dashboard">`
  // and bounce field_staff into UnauthorizedPage even though they
  // signed in successfully.
  const [searchParams] = useSearchParams()
  const explicitRedirectTo = searchParams.get('redirect_to')
  const microsoftHref =
    explicitRedirectTo !== null ? buildLoginUrl(explicitRedirectTo) : '/auth/login'

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    try {
      const body = await apiFetch<{ redirectTo?: string }>('/auth/credentials', {
        method: 'POST',
        // `redirect_to` is null when the URL has no param — the
        // backend's `sanitiseReturnTo` accepts null and falls back to
        // its own default. Hardcoding "/" here would re-introduce the
        // admin-bias the Microsoft path was just fixed to avoid.
        json: { email, password, redirect_to: explicitRedirectTo },
      })
      // Real navigation seam (apps/frontend/src/hooks/api.ts) so the
      // new session cookie is sent on the next request; client-side
      // routing would skip that round-trip and downstream guards
      // would still see the unauthenticated state until reload.
      navigateTo(body.redirectTo ?? '/')
    } catch (err) {
      // The credentials endpoint returns `{ error }` not the
      // Encore-standard `{ code, message, details }`, so apiFetch's
      // ApiError ends up with code=undefined. Pull the discriminator
      // off err.body instead so the alert copy can specialise per
      // case (invalid_credentials vs missing_fields vs not_in_dev_env).
      let errorCode: string | undefined
      if (err instanceof ApiError) {
        const apiBody = err.body as { error?: unknown } | null
        if (apiBody !== null && typeof apiBody === 'object' && typeof apiBody.error === 'string') {
          errorCode = apiBody.error
        }
      }
      setError(describeCredentialsError(errorCode))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main
      className="relative flex min-h-screen flex-col bg-[color:var(--color-cc-forest)] text-[color:var(--color-cc-cream)]"
      data-screen-label="Login"
    >
      {/* Hero photo background with forest overlay. The <img> sits
          below content via z-0; the dark gradient on top guarantees
          contrast for the header chrome regardless of the underlying
          photo. */}
      <div aria-hidden="true" className="absolute inset-0 z-0 overflow-hidden">
        {!imgFailed && (
          <img
            src={HERO_IMAGE}
            alt=""
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        )}
        <div
          className="absolute inset-0 bg-gradient-to-b from-[color:var(--color-cc-forest)]/55 to-[color:var(--color-cc-forest)]/85"
        />
      </div>

      {/* Top bar — logo left, help link right. Wordmark hides below
          ~520px to avoid colliding with the help icon on narrow
          screens (BrandHeader's wordmark is text-current, so it
          inherits the cream from the parent <main>). */}
      <header className="relative z-10 flex items-center justify-between gap-3 px-6 py-5 sm:px-8">
        <BrandHeader variant="topbar" />
        <a
          href="mailto:it@citycare.co.nz"
          // The visible "Need help signing in?" text is hidden under
          // ~sm to leave room for the wordmark, and the icon is
          // decorative (aria-hidden). Without this aria-label the
          // link has no accessible name on small screens.
          aria-label="Need help signing in?"
          className="inline-flex items-center gap-1.5 rounded-full border border-transparent px-2 py-1.5 text-xs font-semibold text-[color:var(--color-cc-cream)]/85 hover:bg-white/10"
        >
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
            <path
              d="M9.5 9.5a2.5 2.5 0 1 1 4 2c-.7.5-1.5 1-1.5 2"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
            <circle cx="12" cy="17" r="1" fill="currentColor" />
          </svg>
          <span className="hidden sm:inline">Need help signing in?</span>
        </a>
      </header>

      {/* Card column. The card stays light against the dark photo so
          credentials and labels read at maximum contrast. */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-6 sm:px-6">
        <div className="w-full max-w-[460px] rounded-3xl border border-black/5 bg-white p-6 shadow-[0_24px_48px_rgba(16,49,43,0.20)] sm:p-10">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-cc-forest)]/70">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
            Sign in
          </span>
          <h1 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight text-[color:var(--color-cc-forest)] sm:text-4xl">
            Welcome back
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-[color:var(--color-cc-forest)]/65">
            Pick up where you left off — your active capture is saved.
          </p>

          {/* Microsoft Entra — primary path. Plain <a> (NOT Link) so
              the browser performs a real navigation to the backend
              route that initiates the OAuth redirect. Same pattern as
              LogoutErrorPage. */}
          <a
            href={microsoftHref}
            className="mt-6 inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-2xl border border-[color:var(--color-cc-forest)]/15 bg-white px-4 py-3.5 text-[15px] font-semibold text-[color:var(--color-cc-charcoal)] hover:border-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-cream)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-forest)]"
          >
            <MicrosoftLogo />
            Continue with Microsoft
          </a>

          <Divider>or sign in with email</Divider>

          {/* Inline alert for credentials failures. role="alert" so
              assistive tech announces the error when it appears
              (per design package README §4). */}
          {error !== null && (
            <p
              role="alert"
              className="mb-4 rounded-xl border border-[color:var(--color-cc-orange)]/30 bg-[color:var(--color-cc-orange)]/10 px-3.5 py-2.5 text-[13px] text-[color:var(--color-cc-charcoal)]"
            >
              {error}
            </p>
          )}

          {/* Email/password — TEMPORARY dev-only path. Backend gates
              POST /auth/credentials to local + cloud-dev envs only.
              Removed when Entra is wired up (see issue 198). */}
          <form onSubmit={handleSubmit}>
            <Field id="login-email" label="Work email">
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder="you@citycare.co.nz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block min-h-[48px] w-full rounded-xl border border-[color:var(--color-cc-forest)]/15 bg-white px-3.5 text-[15px] text-[color:var(--color-cc-charcoal)] placeholder:text-[color:var(--color-cc-forest)]/40 focus-visible:border-[color:var(--color-cc-forest)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
              />
            </Field>

            <Field id="login-password" label="Password">
              <div className="flex items-center rounded-xl border border-[color:var(--color-cc-forest)]/15 bg-white px-3.5 focus-within:border-[color:var(--color-cc-forest)] focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[color:var(--color-cc-lime)]">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block min-h-[48px] flex-1 bg-transparent text-[15px] text-[color:var(--color-cc-charcoal)] placeholder:text-[color:var(--color-cc-forest)]/40 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="rounded px-2 py-1 text-xs font-semibold text-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-forest)]/5"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>

            <div className="mt-1 mb-4 flex items-center justify-between text-[13px]">
              <label className="inline-flex cursor-pointer items-center gap-2 text-[color:var(--color-cc-charcoal)]">
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-4 w-4 accent-[color:var(--color-cc-forest)]"
                />
                <span>Keep me signed in</span>
              </label>
              {/* Forgot-password link points at a SPA path (NOT under
                  /auth/* — that prefix is owned by the Vite dev proxy
                  + Encore backend). The route is unimplemented, so a
                  click currently lands on NotFoundPage; preserved
                  visually because the design surfaces the affordance
                  alongside the email/password fallback. */}
              <Link
                to="/forgot-password"
                className="font-semibold text-[color:var(--color-cc-forest)] hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-[color:var(--color-cc-forest)] px-4 py-3.5 text-[15px] font-semibold text-[color:var(--color-cc-lime)] hover:bg-[color:var(--color-cc-teal)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Signing in\u2026' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <footer className="relative z-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-6 py-5 text-[12px] text-[color:var(--color-cc-cream)]/55">
        <span>© Citycare Property 2026</span>
        <a href="/privacy" className="hover:underline">Privacy</a>
        <a href="/terms" className="hover:underline">Terms</a>
      </footer>
    </main>
  )
}

function MicrosoftLogo() {
  // 2×2 tile mark. Microsoft brand colours are tokenised in
  // src/index.css under --color-ms-* so the no-hardcoded-colours
  // gate stays green; the values themselves are brand-locked and
  // must not be substituted (see comment in index.css).
  return (
    <span
      aria-hidden="true"
      className="grid h-[18px] w-[18px] flex-none grid-cols-2 gap-px"
    >
      <span className="block bg-[color:var(--color-ms-red)]" />
      <span className="block bg-[color:var(--color-ms-green)]" />
      <span className="block bg-[color:var(--color-ms-blue)]" />
      <span className="block bg-[color:var(--color-ms-yellow)]" />
    </span>
  )
}

function Divider({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-cc-forest)]/45">
      <span className="h-px flex-1 bg-[color:var(--color-cc-forest)]/10" />
      <span>{children}</span>
      <span className="h-px flex-1 bg-[color:var(--color-cc-forest)]/10" />
    </div>
  )
}

function Field({
  id,
  label,
  children,
}: {
  id: string
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="mb-3.5 flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-xs font-semibold tracking-wide text-[color:var(--color-cc-forest)]"
      >
        {label}
      </label>
      {children}
    </div>
  )
}
